import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Loader2, Send } from 'lucide-react';
import { useState } from 'react';
import { TOKEN_LANGUAGES, phoneIsUnusable, phoneProblem } from '@jt/domain';
import { api } from '../lib/api';
import { Modal } from './Modal';
import { useSession } from '../lib/session';
import type { LandlordToken } from '../lib/types';

/**
 * One landlord, and every token that has ever reached them.
 *
 * ## Why this exists
 *
 * Because "what has this landlord actually been sent" had no answer anywhere.
 * The tokens were spread across two tables — the ones that matched a payment,
 * and the ones forwarded off the handset that matched nothing — and a person
 * asking about one meter had to look in both, in two different screens, and
 * work out for themselves which rows were the same token twice.
 *
 * ## Why the digits are on the screen
 *
 * Because the button beside them is the point. Somebody looks at a token, sees
 * it has not gone, and sends it. Reading this is behind `billing:read`;
 * sending is a separate capability and a separate press, and the API refuses a
 * second send whatever this screen believes.
 */
export function LandlordDetails({ meterId, onClose }: { meterId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);

  const details = useQuery({
    queryKey: ['landlord-tokens', meterId],
    queryFn: () => api.landlordTokens(meterId),
    /*
     * Polled while open, because a token can land from the paying handset
     * while somebody is looking at this very list.
     */
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });

  /*
   * Which token is being sent, and in which language.
   *
   * Asked rather than assumed: a landlord who reads Kiswahili and is sent
   * English will still key the digits in, but a message somebody has to
   * puzzle over is a meter that stays dark longer than it needed to. The
   * choice is one tap and it is remembered for the next one this visit.
   */
  const [sending, setSending] = useState<LandlordToken | null>(null);
  const [language, setLanguage] = useState<string>('en');

  const send = useMutation({
    mutationFn: (token: LandlordToken) =>
      api.sendLandlordToken(meterId, token.source, token.id, language),
    onSuccess: async (result) => {
      setError(null);
      setSent(result.message);
      setSending(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['landlord-tokens', meterId] }),
        queryClient.invalidateQueries({ queryKey: ['power-outstanding'] }),
        queryClient.invalidateQueries({ queryKey: ['forwarded-messages'] }),
      ]);
    },
    onError: (e: Error) => {
      setSent(null);
      setError(e.message);
    },
  });

  const meter = details.data?.meter;
  const tokens = details.data?.tokens ?? [];
  const maySend = can('payment:record');

  return (
    <Modal
      title={meter ? meter.landlordName : 'Landlord'}
      subtitle={meter ? `${meter.label} · meter ${meter.meterNumber}` : undefined}
      onClose={onClose}
      footer={
        <button type="button" className="btn-quiet" onClick={onClose}>
          Close
        </button>
      }
    >
      <div className="space-y-4" data-landlord-details>
        {details.isPending && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Looking&hellip;
          </p>
        )}

        {error && (
          <p className="rounded-xl border border-danger-300 bg-danger-50 p-3 text-sm text-danger-700">
            {error}
          </p>
        )}
        {sent && !error && (
          <p className="rounded-xl border border-secondary-300 bg-secondary-50 p-3 text-sm text-secondary-900">
            {sent}
          </p>
        )}

        {/* ── Who they are ───────────────────────────────────────────── */}
        {meter && (
          <dl className="grid gap-2 rounded-xl border border-border p-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-xs text-muted-foreground">Phone</dt>
              <dd className="font-mono">{meter.landlordPhone}</dd>
              {/*
                Said here because this is the screen somebody is on when a
                token is about to go out. A number that cannot receive one is
                worth knowing before the send, not after it silently fails.
              */}
              {phoneProblem(meter.landlordPhone) && (
                <dd
                  className={`mt-0.5 flex items-start gap-1 text-xs ${
                    phoneIsUnusable(meter.landlordPhone)
                      ? 'text-danger-700'
                      : 'text-muted-foreground'
                  }`}
                >
                  <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
                  {phoneProblem(meter.landlordPhone)}
                </dd>
              )}
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Meter</dt>
              <dd className="font-mono">{meter.meterNumber}</dd>
            </div>
            {meter.landlordEmail && (
              <div>
                <dt className="text-xs text-muted-foreground">Email</dt>
                <dd>{meter.landlordEmail}</dd>
              </div>
            )}
            <div>
              <dt className="text-xs text-muted-foreground">Site</dt>
              <dd>{meter.siteLabel ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Last paid</dt>
              <dd className="tabular-nums">
                {meter.lastPaid ? `${meter.lastPaid}${meter.lastPaidAt ? ` on ${new Date(meter.lastPaidAt).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}` : 'Not paid yet'}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Next due</dt>
              <dd className="tabular-nums">{meter.nextDue ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Topped up</dt>
              <dd>{meter.isActive ? 'Yes' : 'No longer'}</dd>
            </div>
            {meter.notes && (
              <div className="sm:col-span-2">
                <dt className="text-xs text-muted-foreground">Notes</dt>
                <dd>{meter.notes}</dd>
              </div>
            )}
          </dl>
        )}

        {/* ── The tokens ─────────────────────────────────────────────── */}
        <div>
          <h3 className="text-sm font-medium">
            Tokens received
            {details.data && details.data.waiting > 0 && (
              <span className="ml-2 rounded-full border border-accent-300 bg-accent-50 px-2 py-0.5 text-xs text-accent-900">
                {details.data.waiting} not sent
              </span>
            )}
          </h3>

          {!details.isPending && tokens.length === 0 ? (
            <p className="mt-2 rounded-xl border border-border bg-muted p-3 text-sm text-muted-foreground">
              No token has arrived for this meter yet. When one is forwarded off the paying
              handset it appears here, and goes to {meter?.landlordName ?? 'the landlord'} on its
              own.
            </p>
          ) : (
            <ul className="mt-2 space-y-2">
              {tokens.map((one) => (
                <li
                  key={`${one.source}:${one.id}`}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border p-3"
                >
                  <div className="min-w-0">
                    <p className="font-mono text-sm">{one.token ?? '—'}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {[
                        new Date(one.at).toLocaleString('en-KE'),
                        one.units === null ? null : `${one.units} units`,
                        one.amount,
                        one.receipt,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                    {one.sentAt && (
                      <p className="mt-0.5 flex items-center gap-1 text-xs text-secondary-800">
                        <Check className="h-3 w-3" aria-hidden="true" />
                        Sent {new Date(one.sentAt).toLocaleString('en-KE')}
                        {one.sentTo ? ` to ${one.sentTo}` : ''}
                      </p>
                    )}
                  </div>

                  {maySend && (
                    <button
                      type="button"
                      className={one.canSend ? 'btn-primary shrink-0' : 'btn-quiet shrink-0'}
                      disabled={!one.canSend || send.isPending}
                      /*
                       * A disabled button has to say why. "Already gone" is the
                       * only reason there is, and it is worth reading before
                       * somebody wonders whether the page is broken.
                       */
                      title={
                        one.blockedBecause ??
                        `Send this token to ${meter?.landlordName ?? 'the landlord'}`
                      }
                      onClick={() => setSending(one)}
                    >
                      {send.isPending && send.variables?.id === one.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      ) : (
                        <Send className="h-4 w-4" aria-hidden="true" />
                      )}
                      {one.sentAt ? 'Sent' : 'Send'}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          <p className="mt-2 text-xs text-muted-foreground">
            A token can only go once. Sending a second one makes a landlord believe the first
            failed, and keying the old digits into a meter locks it for the period.
          </p>

          {/*
            * The language, asked at the moment of sending.
            *
            * Inline rather than a second dialog on top of this one: it is one
            * question with two answers, and a stack of modals to answer it
            * would be heavier than the thing being decided.
            */}
          {sending && (
            <div className="mt-3 rounded-xl border border-primary-300 bg-primary-50 p-3">
              <p className="text-sm font-medium">
                Which language does {meter?.landlordName ?? 'the landlord'} read?
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                The token, the meter number and the figures are the same either way &mdash; only
                the words around them change.
              </p>

              <div className="mt-2 flex flex-wrap gap-2">
                {TOKEN_LANGUAGES.map((one) => (
                  <button
                    key={one.value}
                    type="button"
                    className={
                      language === one.value
                        ? 'btn-primary px-3 py-1 text-xs'
                        : 'btn-quiet px-3 py-1 text-xs'
                    }
                    onClick={() => setLanguage(one.value)}
                  >
                    {one.label}
                  </button>
                ))}
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn-primary px-3 py-1 text-xs"
                  disabled={send.isPending}
                  onClick={() => send.mutate(sending)}
                >
                  {send.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  ) : (
                    <Send className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                  Send in {TOKEN_LANGUAGES.find((one) => one.value === language)?.label}
                </button>
                <button
                  type="button"
                  className="btn-quiet px-3 py-1 text-xs"
                  disabled={send.isPending}
                  onClick={() => setSending(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
